/// <reference path="../types/chromecast.d.ts" />
import { useCallback, useEffect, useState, useRef } from "react";

// ============================================================================
// CHROMECAST SETUP INSTRUCTIONS:
// ============================================================================
// 1. Go to https://cast.google.com/publish/ and register as a Cast developer ($5 one-time fee)
// 2. Click "Add New Application" → "Custom Receiver"
// 3. Set the Receiver URL to your deployed app's /cast route, e.g.:
//    https://your-app.web.app/cast
// 4. Copy the Application ID you receive and paste it below
// 5. Add your test devices in the Cast Console for development
// ============================================================================

// Jeoparty Custom Receiver Application ID
// Registered at: https://cast.google.com/publish/
const CAST_APP_ID = "82ADCE54";

// Custom message namespace for sending game info to receiver
const CAST_NAMESPACE = "urn:x-cast:com.jeoparty.game";

declare global {
  interface Window {
    __onGCastApiAvailable?: (isAvailable: boolean) => void;
    cast?: typeof cast;
    chrome?: {
      cast?: typeof chrome.cast;
    };
  }
}

export type CastState =
  | "unavailable"
  | "available"
  | "connecting"
  | "connected";

// Store pending game ID to send when connected
let pendingGameId: string | null = null;

export const useChromecast = () => {
  const [castState, setCastState] = useState<CastState>("unavailable");
  const [castSession, setCastSession] =
    useState<cast.framework.CastSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Heartbeat functions to keep connection alive
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback((session: cast.framework.CastSession | null) => {
    stopHeartbeat(); // Clear any existing heartbeat
    
    if (!session) return;

    // Send heartbeat every 30 seconds to prevent idle disconnect
    heartbeatIntervalRef.current = setInterval(() => {
      try {
        if (session) {
          session.sendMessage(CAST_NAMESPACE, {
            type: "HEARTBEAT",
          });
        }
      } catch (e) {
        // Ignore heartbeat errors - connection may have closed
        console.debug("Heartbeat send failed:", e);
      }
    }, 30000); // Every 30 seconds
  }, [stopHeartbeat]);

  useEffect(() => {
    if (window.cast && window.chrome?.cast) {
      initializeCastApi();
      return;
    }

    // Set up the callback for when Cast SDK loads
    window.__onGCastApiAvailable = (isAvailable: boolean) => {
      if (isAvailable) {
        // Wait a bit for chrome.cast to be fully ready
        let attempts = 0;
        const checkAndInit = () => {
          attempts++;
          if (window.cast && window.chrome?.cast) {
            initializeCastApi();
          } else if (attempts < 50) {
            setTimeout(checkAndInit, 100);
          } else {
            setCastState("unavailable");
            setError("Cast SDK failed to fully initialize");
          }
        };
        checkAndInit();
      } else {
        setCastState("unavailable");
        setError("Chromecast not available on this device");
      }
    };

    // Load Cast SDK if not already present
    if (!document.querySelector('script[src*="cast_sender"]')) {
      const script = document.createElement("script");
      script.src =
        "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const initializeCastApi = useCallback(() => {
    if (isInitialized) return;
    if (!window.cast || !window.chrome?.cast) return;

    const context = cast.framework.CastContext.getInstance();

    try {
      context.setOptions({
        receiverApplicationId: CAST_APP_ID,
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });
    } catch {
      // Options may already be set
    }

    // Listen for cast state changes
    context.addEventListener(
      cast.framework.CastContextEventType.CAST_STATE_CHANGED,
      (event: cast.framework.CastStateEventData) => {
        switch (event.castState) {
          case cast.framework.CastState.NO_DEVICES_AVAILABLE:
            setCastState("unavailable");
            setCastSession(null);
            stopHeartbeat();
            break;
          case cast.framework.CastState.NOT_CONNECTED:
            setCastState("available");
            setCastSession(null);
            stopHeartbeat();
            break;
          case cast.framework.CastState.CONNECTING:
            setCastState("connecting");
            break;
          case cast.framework.CastState.CONNECTED:
            setCastState("connected");
            const session = context.getCurrentSession();
            setCastSession(session);

            // If there's a pending game ID, send it now!
            if (pendingGameId && session) {
              try {
                session.sendMessage(CAST_NAMESPACE, {
                  type: "LOAD_GAME",
                  gameId: pendingGameId,
                });
                pendingGameId = null;
              } catch {
                // Message send failed
              }
            }

            // Start heartbeat to keep connection alive
            startHeartbeat(session);
            break;
        }
      }
    );

    // Set initial state
    const initialState = context.getCastState();
    if (initialState === cast.framework.CastState.NOT_CONNECTED) {
      setCastState("available");
    } else if (initialState === cast.framework.CastState.CONNECTED) {
      const currentSession = context.getCurrentSession();
      setCastState("connected");
      setCastSession(currentSession);
      // Start heartbeat if already connected
      if (currentSession) {
        startHeartbeat(currentSession);
      }
    }

    setIsInitialized(true);
  }, [startHeartbeat]);

  // Send message to receiver
  const sendMessageToReceiver = useCallback((gameId: string) => {
    if (!window.cast || !window.chrome?.cast) return false;

    const context = cast.framework.CastContext.getInstance();
    const session = context.getCurrentSession();

    if (session) {
      try {
        session.sendMessage(CAST_NAMESPACE, {
          type: "LOAD_GAME",
          gameId: gameId,
        });
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }, []);

  const requestSession = useCallback(
    async (gameId?: string) => {
      if (!window.cast || !window.chrome?.cast) {
        setError("Cast SDK not loaded");
        return false;
      }

      const context = cast.framework.CastContext.getInstance();

      try {
        // If already connected, just send the message
        if (castState === "connected") {
          if (gameId) sendMessageToReceiver(gameId);
          return true;
        }

        if (castState === "connecting") {
          return false;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const options = (context as any).getCastOptions?.();
        if (!options?.receiverApplicationId) {
          try {
            context.setOptions({
              receiverApplicationId: CAST_APP_ID,
              autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
            });
          } catch {
            // Options set failed
          }
        }

        // Store the gameId so we can send it when CONNECTED fires
        if (gameId) {
          pendingGameId = gameId;
        }

        await context.requestSession();

        if (gameId) {
          // Retry sending message a few times if needed
          let attempts = 0;
          const sendLoop = setInterval(() => {
            attempts++;
            const success = sendMessageToReceiver(gameId);
            if (success || attempts >= 5) {
              clearInterval(sendLoop);
            }
          }, 1000);
        }

        return true;
      } catch (e: unknown) {
        const error = e as { code?: string; message?: string };
        if (error?.code !== "cancel") {
          setError(error?.message || "Failed to connect");
        }
        return false;
      }
    },
    [castState, sendMessageToReceiver]
  );

  const endSession = useCallback(() => {
    stopHeartbeat();
    
    if (!window.cast) return;

    const context = cast.framework.CastContext.getInstance();
    const session = context.getCurrentSession();

    if (session) {
      session.endSession(true);
      setCastSession(null);
      setCastState("available");
    }
  }, [stopHeartbeat]);

  // Cleanup heartbeat on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
    };
  }, [stopHeartbeat]);

  const castUrl = useCallback(
    (url: string) => {
      if (!castSession) {
        setError("No active cast session");
        return false;
      }

      const mediaInfo = new chrome.cast.media.MediaInfo(url, "text/html");
      mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
      mediaInfo.metadata.title = "Jeoparty Game Board";

      const request = new chrome.cast.media.LoadRequest(mediaInfo);

      castSession.loadMedia(request).then(
        () => {},
        () => {
          setError("Failed to cast game board");
        }
      );

      return true;
    },
    [castSession]
  );

  const sendMessage = useCallback(
    (namespace: string, message: object) => {
      if (!castSession) {
        return false;
      }

      castSession.sendMessage(namespace, message);
      return true;
    },
    [castSession]
  );

  return {
    castState,
    castSession,
    error,
    requestSession,
    endSession,
    castUrl,
    sendMessage,
    isAvailable: castState !== "unavailable",
    isConnected: castState === "connected",
    isInitialized,
  };
};
